import React, {useCallback, useEffect, useMemo, useState} from "react";
import "../../styles/current-orders.css";

import {apiFetch} from "../../utils/api";
import {BASE_URLS} from "../../config/urls";
import {Orders} from "../../models/Orders";
import {Company} from "../../models/Company";
import {LabelsService} from "../../services/LabelsService";
import {OrderInfoModal} from "../modal/OrderInfoModal";
import {Person} from "../../models/Person";
import "../../static/Roboto-normal";
import "../../static/Roboto-bold";

type SortDir = "asc" | "desc";

type OrdersPageBaseProps = {
    title: string;
    subtitle?: string;
    badgeText?: string;

    ordersEndpoint: string;
    companiesEndpoint?: string; // по умолчанию `${BASE_URLS.api}/companies`

    hints: React.ReactNode; // подсказки справа (можно ul/li)
    emptyTitle?: string;
    emptySubtitle?: string;

    // если в истории не надо "Удалить/Изменить/Оплатить" — можно выключить
    allowActions?: boolean;

    person: Person | null;
};

const STATUS_OPTIONS = [
    {value: "all", label: "Все статусы"},
    {value: "Ожидает на ФФ", label: "Ожидает на ФФ"},
    {value: "Ожидает оплаты", label: "Ожидает оплаты"},
    {value: "Доставка на склад", label: "Доставка на склад"},
    {value: "Доставлен", label: "Доставлен"},
];

const SORT_OPTIONS = [
    {value: "departureDate", label: "Дата доставки"},
    {value: "acceptanceDate", label: "Приёмный день"},
    {value: "entity", label: "ИП / ООО / Самозанятый"},
    {value: "store", label: "МП"},
    {value: "sendCity", label: "Склад"},
    {value: "volume", label: "Объём, м³"},
    {value: "willTaken", label: "Забор груза"},
    {value: "price", label: "Стоимость"},
    {value: "paymentSite", label: "Способ оплаты"},
    {value: "paymentStatus", label: "Статус оплаты"},
    {value: "status", label: "Статус доставки"},
];

function formatDate(dateInput: Date | string): string {
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
}

function getStatusClass(status?: string) {
    switch (status) {
        case "Ожидает на ФФ":
            return "is-pending-ff";
        case "Ожидает оплаты":
            return "is-pending-payment";
        case "Доставка на склад":
            return "is-delivery";
        case "Доставлен":
            return "is-delivered";
        default:
            return "is-default";
    }
}

function normalizePrice(val: any) {
    return Number(String(val ?? "").replace(/\s+/g, "")) || 0;
}

export const OrdersPageBase: React.FC<OrdersPageBaseProps> = ({
                                                                  title,
                                                                  subtitle = "Здесь отображаются поставки, статус оплаты и доставки.",
                                                                  badgeText = "Поставки",

                                                                  ordersEndpoint,
                                                                  companiesEndpoint = `${BASE_URLS.api}/companies`,

                                                                  hints,
                                                                  emptyTitle = "Нет заказов",
                                                                  emptySubtitle = "Когда появятся записи — они будут здесь.",

                                                                  allowActions = true,
                                                                  person,
                                                              }) => {
    const [orders, setOrders] = useState<Orders[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);

    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [sortColumn, setSortColumn] = useState<string>("departureDate");
    const [sortDirection, setSortDirection] = useState<SortDir>("desc");

    const [isOrderModalOpen, setOrderModalOpen] = useState(false);
    const [activeOrder, setActiveOrder] = useState<Orders | null>(null);

    const openOrderModal = useCallback((order: Orders) => {
        setActiveOrder(order);
        setOrderModalOpen(true);
    }, []);

    const closeOrderModal = useCallback(() => {
        setOrderModalOpen(false);
        setActiveOrder(null);
    }, []);

    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        setErr("");

        apiFetch(ordersEndpoint)
            .then((resp) => resp.json())
            .then((data) => {
                if (cancelled) return;
                setOrders(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (cancelled) return;
                setErr("Не удалось загрузить поставки");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        apiFetch(companiesEndpoint)
            .then((resp) => resp.json())
            .then((data) => {
                if (cancelled) return;
                setCompanies(Array.isArray(data) ? data : []);
            })
            .catch(console.error);

        return () => {
            cancelled = true;
        };
    }, [ordersEndpoint, companiesEndpoint]);

    const downloadLabels = useCallback((order: Orders) => {
        LabelsService.downloadOrderLabels(order);
    }, []);

    const payOrder = useCallback((orderId: number) => {
        window.location.assign(`${BASE_URLS.app}/payment/${orderId}`);
    }, []);

    const deleteOrder = useCallback(async (order: Orders) => {
        try {
            await apiFetch(`${BASE_URLS.api}/delete/${order.id}`, {
                method: "DELETE",
                headers: {"Content-Type": "application/json"},
            });
            setOrders((prev) => prev.filter((x) => x.id !== order.id));
        } catch (e) {
            console.error(e);
            setErr("Не удалось удалить поставку");
        }
    }, []);

    const filtered = useMemo(() => {
        return statusFilter === "all" ? orders : orders.filter((o) => o.status === statusFilter);
    }, [orders, statusFilter]);

    const sorted = useMemo(() => {
        const list = [...filtered];

        list.sort((a: any, b: any) => {
            let A: any = a?.[sortColumn];
            let B: any = b?.[sortColumn];

            if (sortColumn === "price") {
                A = normalizePrice(A);
                B = normalizePrice(B);
            }

            if (sortColumn === "acceptanceDate" || sortColumn === "departureDate") {
                A = new Date(A).getTime();
                B = new Date(B).getTime();
            }

            if (typeof A === "string") A = A.toLowerCase();
            if (typeof B === "string") B = B.toLowerCase();

            if (A < B) return sortDirection === "asc" ? -1 : 1;
            if (A > B) return sortDirection === "asc" ? 1 : -1;
            return 0;
        });

        return list;
    }, [filtered, sortColumn, sortDirection]);

    return (
        <div className="current-orders-page">
            <div className="current-orders-grid">
                {/* MAIN */}
                <div className="current-orders-main">
                    <div className="current-orders-head card">
                        <div className="badge">{badgeText}</div>

                        <div className="head-row">
                            <div>
                                <h1 className="title">{title}</h1>
                                <p className="subtitle">{subtitle}</p>
                            </div>
                        </div>

                        {/* controls */}
                        <div className="controls">
                            <div className="field">
                                <div className="label">Фильтр по статусу</div>
                                <select
                                    className="select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    {STATUS_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="field">
                                <div className="label">Сортировка</div>
                                <select
                                    className="select"
                                    value={sortColumn}
                                    onChange={(e) => setSortColumn(e.target.value)}
                                >
                                    {SORT_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="field field--dir">
                                <div className="label">Направление</div>
                                <button
                                    type="button"
                                    className={`btn-dir ${sortDirection === "desc" ? "is-desc" : ""}`}
                                    onClick={() => setSortDirection((p) => (p === "asc" ? "desc" : "asc"))}
                                    title={sortDirection === "asc" ? "По возрастанию" : "По убыванию"}
                                >
                                    <span className="dir-icon" aria-hidden="true">▲</span>
                                    <span className="dir-text">{sortDirection === "asc" ? "ASC" : "DESC"}</span>
                                </button>
                            </div>
                        </div>

                        {err && <div className="alert">{err}</div>}
                    </div>

                    {/* LIST */}
                    <div className="orders-list">
                        {loading && (
                            <div className="card skeleton">
                                <div className="skeleton-line w40"/>
                                <div className="skeleton-line w70"/>
                                <div className="skeleton-line w55"/>
                            </div>
                        )}

                        {!loading && sorted.length === 0 && (
                            <div className="card empty">
                                <div className="empty-title">{emptyTitle}</div>
                                <div className="empty-sub">{emptySubtitle}</div>
                            </div>
                        )}

                        {!loading &&
                            sorted.map((o) => (
                                <div key={o.id} className="card order-card">
                                    <div className="order-top">
                                        <div className="order-title">{o.entity}</div>
                                        <span className={`status ${getStatusClass(o.status)}`}>{o.status || "—"}</span>
                                    </div>

                                    <div className="order-meta">
                                        <div className="meta-item">
                                            <div className="meta-label">Приёмный день</div>
                                            <div className="meta-value">{formatDate(o.acceptanceDate)}</div>
                                        </div>

                                        <div className="meta-item">
                                            <div className="meta-label">Дата доставки</div>
                                            <div className="meta-value">{formatDate(o.departureDate)}</div>
                                        </div>

                                        <div className="meta-item">
                                            <div className="meta-label">МП / Склад</div>
                                            <div className="meta-value">
                                                {o.store} / {o.sendCity}
                                            </div>
                                        </div>

                                        <div className="meta-item">
                                            <div className="meta-label">Объём</div>
                                            <div className="meta-value">{o.volume} м³</div>
                                        </div>

                                        <div className="meta-item">
                                            <div className="meta-label">Забор груза</div>
                                            <div className="meta-value">{o.willTaken ? "Да" : "Нет"}</div>
                                        </div>

                                        <div className="meta-item">
                                            <div className="meta-label">Стоимость</div>
                                            <div className="meta-value meta-value--price">{o.price}</div>
                                        </div>

                                        <div className="meta-item">
                                            <div className="meta-label">Способ оплаты</div>
                                            <div className="meta-value">{o.paymentSite ? "онлайн" : "оффлайн"}</div>
                                        </div>

                                        <div className="meta-item">
                                            <div className="meta-label">Статус оплаты</div>
                                            <div
                                                className="meta-value">{o.paymentStatus ? "Оплачено ✅" : "Не оплачено"}</div>
                                        </div>
                                    </div>

                                    <div className="order-actions">
                                        {allowActions && o.paymentSite && !o.paymentStatus && (
                                            <button className="btn-primary" onClick={() => payOrder(o.id!)}>
                                                Оплатить
                                            </button>
                                        )}

                                        <button className="btn-secondary" onClick={() => openOrderModal(o)}>
                                            Просмотр
                                        </button>

                                        <button className="btn-secondary" onClick={() => downloadLabels(o)}>
                                            Скачать этикетки
                                        </button>

                                        {allowActions && o.changeable && (
                                            <button className="btn-danger" onClick={() => deleteOrder(o)}>
                                                Удалить
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>

                {/* SIDE */}
                <div className="schedule-side">
                    <div className="schedule-side-card">
                        <div className="schedule-side-title">Подсказки</div>
                        {hints}
                    </div>
                </div>
            </div>

            {person && (
                <OrderInfoModal
                    isOpen={isOrderModalOpen}
                    onClose={closeOrderModal}
                    order={activeOrder}
                    companies={companies}
                    onDelete={(ord: Orders) => {
                        deleteOrder(ord);
                        closeOrderModal();
                    }}
                    onDownloadLabels={(ord: Orders) => downloadLabels(ord)}
                    onOrderUpdated={(updated: Orders) => {
                        setOrders((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
                        setActiveOrder(updated);
                    }}
                    person={person}
                />
            )}
        </div>
    );
};
